import { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { deleteReport } from 'services/nobo'
import { useModal } from 'hooks/useModal'
import { useNotification } from 'components/Notification'

/**
 * Custom hook to manage the confirmation and modal logic for deleting a report.
 *
 * This hook provides state and handlers to control the delete confirmation modal,
 * including tracking which report is being deleted, the user's confirmation input,
 * and whether the deletion may proceed (user must type the matching CUSIP to confirm).
 * @returns {Object} An object containing:
 *   - isOpen {boolean}: Whether the modal is open.
 *   - onOpen {function(report: Object): void}: Opens the modal for a specific report.
 *   - onClose {function(): void}: Closes the modal and resets state.
 *   - reportToDelete {Object|undefined}: The report object selected for deletion.
 *   - setConfirmText {function(string): void}: Updates the confirmation text input.
 *   - canDelete {boolean}: True if the entered confirmation text matches the report's CUSIP.
 */
function useDeleteConfirmation() {
  const [reportToDelete, setReportToDelete] = useState() // the report object selected for deletion
  const [confirmText, setConfirmText] = useState('') // the text (e.g. CUSIP) entered by the user to confirm the deletion
  const { open, handleOpen, handleClose } = useModal()

  const onOpen = report => {
    setReportToDelete(report)
    handleOpen()
  }

  const onClose = () => {
    setReportToDelete(undefined)
    setConfirmText('')
    handleClose()
  }

  // User must type the matching CUSIP (case-insensitive) to enable delete action
  const canDelete = reportToDelete?.cusip.toLowerCase() === confirmText.toLowerCase()

  const onChangeConfirmText = e => setConfirmText(e.target.value)

  return { canDelete, isOpen: open, onOpen, onClose, reportToDelete, onChangeConfirmText }
}

/**
 * Custom hook to handle the actual deletion of a report with notification feedback.
 * @param {Object} params - Hook parameters.
 * @param {Function} params.onClose - Function to call when deletion operation finishes (success or error).
 * @returns {Object} An object containing:
 *   - isDeleting {boolean}: True if the deletion is in progress.
 *   - deleteReport {function}: Mutation function to trigger the report deletion (call with report id).
 */
function useDeleteReport({ onClose }) {
  const { createNotification } = useNotification()
  const queryClient = useQueryClient()

  const { mutate, isLoading, error } = useMutation({
    mutationFn: deleteReport,
    onSuccess: () => {
      createNotification({
        message: 'Report deleted successfully',
        variant: 'success',
      })
      // refresh the nobo reports query to get the latest data
      queryClient.invalidateQueries({ queryKey: ['getNoboReports'] })
      onClose()
    },
    onError: error => {
      createNotification({
        message: `Error deleting report: ${error?.toString()}`,
        variant: 'error',
      })
    },
  })

  return {
    deleteReport: mutate,
    error,
    isDeleting: isLoading,
  }
}

export { useDeleteReport, useDeleteConfirmation }
